

import React, { useState, useEffect } from 'react';
import { clickable } from "../../utils/a11y";
import "../../styles/Careplan.css";
import Loader from '../../utils/loader';
import { groupBy } from '../../utils/groupBy';

const CarePlan = (props) => {
  const [data, setData] = useState(props.carePlanData || null);
  const [activeLoader, setActiveLoader] = useState(false);


  useEffect(() => {
    if (props.carePlanData) {
      setData(props.carePlanData);
    }
  }, [props.carePlanData]);

  const prescriptions = data && data['carePlan'] && data['carePlan']['presTypes']

  const sortCategories = (a, b) => {
    if (a[0] === 'null') return -1;
    if (b[0] === 'null') return 1;
    return 0;
  };

    const frequencyMap = {
      qd: "once daily",
      BID: "twice daily",
      TID: "three times daily",
      QID: "four times daily",
      qHS: "daily at bedtime",
      PRN: "as needed"
    };
    const freqRegex = /\b(qd|BID|TID|QID|qHS|PRN)\b$/i;

    const getDrugName = (name) => {
      return name.replace(freqRegex, '').trim();
    };

    const getInstruction = (name) => {
      const match = name.match(freqRegex);
      if (match) {
        const abbr = match[0];
        const key = Object.keys(frequencyMap).find(k => k.toLowerCase() === abbr.toLowerCase());
        return `Take ${frequencyMap[key] || abbr}`;
      }
      return null;
    };

  function formatDateToEST(dateString) {
    if (!dateString) return '';

    let date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      date = new Date(dateString.replace(/-/g, '\/'));
    }
    if (isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  }

    const getHeaderIcon = (typeName) => {
      const iconProps = {
        width: "24",
        height: "24",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        style: { color: '#8fa87a', width: '20px', height: '20px' },
        "aria-hidden": "true"
      };

      switch (typeName) {
        case "Medications":
          return (
            <svg {...iconProps}>
              <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
              <path d="m8.5 8.5 7 7" />
            </svg>
          );
        case "Lifestyle Modifications":
          return (
            <svg {...iconProps}>
              <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
            </svg>
          );
        case "Labs":
          return (
            <svg {...iconProps}>
              <path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" />
              <path d="M6.453 15h11.094" /><path d="M8.5 2h7" />
            </svg>
          );
        case "Imaging":
          return (
            <svg {...iconProps}>
              <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" />
              <path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" />
              <path d="M7 12h10" />
            </svg>
          );
        case "Medical Expulsive Therapy": // Often related to kidney stones/flow
          return (
            <svg {...iconProps}>
              <path d="m7 16 3-3 3 3" /><path d="m7 11 3-3 3 3" /><path d="M10 22v-9" /><path d="M10 2h.01" /><path d="M10 6h.01" /><path d="M10 10h.01" />
            </svg>
          );
        case "Topical Therapy": // Cream/Skin application icon
          return (
            <svg {...iconProps}>
              <path d="M15 2H9a2 2 0 0 0-2 2v2h10V4a2 2 0 0 0-2-2Z" />
              <path d="M7 6h10v14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Z" />
              <path d="M12 12v4" /><path d="M10 14h4" />
            </svg>
          );
        case "Psychotherapy & Behavioral Modifications": // Brain/Mind icon
          return (
            <svg {...iconProps}>
              <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .52 5.586 4 4 0 0 0 5.188 3.108 4 4 0 0 0 7.631-1.589 4 4 0 0 0-1.455-7.376A3 3 0 0 0 12 5Z" />
            </svg>
          );
        default: // Generic Clipboard for unknown types
          return (
            <svg {...iconProps}>
              <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            </svg>
          );
      }
    };

  return (
    // <div>
    <>
      {prescriptions ?
          <>
            {Object.keys(prescriptions).map((presType) => (
              <div key={presType} className="prescription-type-container" style={{ marginBottom: '1.5rem' }}>
                {/* Header Section */}
                <div
                  className="header"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    borderRadius: '10px',
                    backgroundColor: '#cfe8d4',
                  }}
                >
                  {getHeaderIcon(prescriptions[presType][0].type.name)}
                  <h5 style={{ margin: 0 }}>{prescriptions[presType][0].type.name}</h5>
                </div>

                {/* List Section */}
                <div style={{ paddingLeft: '12px', paddingTop: '6px' }}>
                  {Object.entries(
                    groupBy(prescriptions[presType], ({ categoryName }) => categoryName)
                  )
                    .sort(sortCategories)
                    .map(([category, items]) =>
                      items.map((item) =>
                        item.indicator ? (
                          <div
                            key={item.presId}
                            style={{
                              cursor: 'pointer',
                              padding: '8px',
                              borderRadius: '10px',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#cfe8d4';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                              e.currentTarget.style.transform = '';
                            }}
                            {...clickable(() =>
                              props.changeView(3, {
                                diagName: data.diagName,
                                presName: item.presName,
                              })
                            )}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '0.75rem' }}>•</span>
                              <div style={{ flex: '1' }}>
                                <p style={{ margin: '0px', fontSize: '0.875rem' }}>
                                  {getDrugName(item.presName)}
                                </p>
                                {getInstruction(item.presName) && (
                                  <p style={{ margin: '0px', fontSize: '0.75rem', opacity: '0.6' }}>
                                    {getInstruction(item.presName)}
                                  </p>
                                )}
                              </div>
                              {category && category !== "null" && (
                                <p style={{ margin: '0px', marginTop: '8px', fontSize: '0.875rem', opacity: '0.7' }}>
                                  {category}
                                </p>
                              )}
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                                style={{ opacity: '0.5', width: '16px', height: '16px', marginTop: '11px' }}
                              >
                                <path d="m9 18 6-6-6-6"></path>
                              </svg>
                            </div>
                          </div>
                        ) : null
                      )
                    )}
                </div>
              </div>
            ))}


              {data?.notes?.length ? (
                <div className="notes-container">
                  <strong>Notes : </strong>
                  <p className="notes-text">{data.notes}</p>
                </div>
              ) : null}
            {data.followUp ? (
              <div
                style={{
                  marginTop: '16px',
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '16px',
                  display: 'flex',           // Turn container into flex
                  flexDirection: 'column',    // Stack children vertically
                  alignItems: 'center',       // Center all children horizontally
                  width: '100%'
                }}
              >
                {/* Follow-up Section */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <svg
                    width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ width: '16px', height: '16px' }}
                  >
                    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                    <path d="M3 3v5h5"></path>
                    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                    <path d="M16 16h5v5"></path>
                  </svg>
                  <p style={{ margin: 0, fontSize: '0.875rem', textAlign: 'center' }}>
                    <span style={{ opacity: 0.7 }}>Follow-up required on/after:</span>{' '}
                    <span style={{ color: '#f87171', fontWeight: '600' }}>
                      {formatDateToEST(data.followUpDate)}
                    </span>
                  </p>
                </div>

                {/* Action Button */}
                <button type="button" className="btn-book" onClick={() => props.changeView(1)}>
                  Book an appointment
                </button>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '16px',
                gap: '8px',
                color: '#10b981',
                width: '100%'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                  select_check_box
                </span>
                <strong style={{ fontSize: '0.875rem' }}>Follow-up not required</strong>
              </div>
            )}

            {/* Dated regardless of whether a follow-up is required. This sat inside
                the follow-up branch above, so a plan with no follow-up carried no
                date at all — and a care plan the patient cannot date is one they
                cannot tell is current. */}
            {data.lastModified && (
              <p style={{ margin: '12px 0 0 0', fontSize: '0.75rem', opacity: 0.6, textAlign: 'center' }}>
                Care plan last updated: {formatDateToEST(data.lastModified)}
              </p>
            )}
          </> : <div style={{
            width: '100%',
            height: '80%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '50%'
          }} className="see-a-doctor">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' }}>
              <h3 style={{ margin: 0 }}><i>No care plan yet</i></h3>
              <p style={{ margin: 0 }}>Your provider writes your care plan at your first appointment.</p>
              <button type="button" className="btn-book" onClick={() => props.changeView(1)}>Book an appointment</button>
            </div>
          </div>
      }
      <Loader active={activeLoader} />
    </>
  );
};

export default CarePlan;

